import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SquishyButton from "../components/SquishyButton";
import { sound } from "../lib/sounds";
import { speak } from "../lib/speech";
import { useGame } from "../context/GameContext";
import api from "../lib/api";
import { Volume2, Lightbulb } from "lucide-react";

const PRAISE = ["Goed gedaan!", "Super!", "Knap hoor!", "Yes, juist!", "Wat goed!"];
const RETRY = ["Probeer het nog eens!", "Bijna! Probeer opnieuw.", "Nog een keer!"];
const pick = (a) => a[Math.floor(Math.random() * a.length)];

// Group of emojis for counting / sums
const EmojiGroup = ({ emoji, n }) => (
  <div className="flex flex-wrap justify-center gap-1.5 max-w-xs">
    {Array.from({ length: n }, (_, i) => (
      <motion.span
        key={i}
        initial={{ scale: 0, rotate: -20 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: i * 0.06, type: "spring", stiffness: 300 }}
        className="text-4xl sm:text-5xl select-none"
      >
        {emoji}
      </motion.span>
    ))}
  </div>
);

// Handles a single question for count/sum/letter/word/pattern lessons.
export const ChoiceRound = ({ round, onComplete }) => {
  const { profile } = useGame();
  const [status, setStatus] = useState("idle"); // idle | correct | wrong
  const [chosen, setChosen] = useState(null);
  const [hint, setHint] = useState(null);
  const [hintLoading, setHintLoading] = useState(false);
  const wrongOnce = useRef(false);

  const speakPrompt = () => {
    if (round.kind === "letter") speak(`Zoek de letter ${round.target}`);
    else if (round.kind === "word") speak(`Welk plaatje is: ${round.word}?`);
    else if (round.kind === "sum") speak(`Hoeveel is ${round.a} ${round.op === "×" ? "keer" : "plus"} ${round.b}?`);
    else if (round.kind === "count") speak("Hoeveel zie je?");
    else if (round.kind === "pattern") speak("Wat komt hierna?");
    else if (round.kind === "color") speak(round.prompt);
    else if (round.kind === "flag") speak("Welk land is dit?");
    else if (round.kind === "colormix") speak(round.prompt);
    else if (round.kind === "classify") speak(round.prompt);
    else if (round.kind === "quiz") speak(round.question);
  };

  useEffect(() => {
    wrongOnce.current = false;
    setStatus("idle");
    setChosen(null);
    setHint(null);
    const t = setTimeout(speakPrompt, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round]);

  const isCorrectVal = (val) => {
    if (round.kind === "word") return val.emoji === round.answer;
    return val === (round.target ?? round.answer);
  };

  const askLuna = async () => {
    if (hintLoading) return;
    setHintLoading(true);
    const opts = round.kind === "word" ? round.options.map((o) => o.word) : round.options.map((o) => String(o));
    const answer = round.kind === "word" ? round.word : String(round.target ?? round.answer);
    try {
      const { data } = await api.post("/ai/hint", {
        session_id: `hint-${profile?.id || "gast"}`,
        age: profile?.age || 6,
        question: round.question || round.prompt || "",
        options: opts,
        answer,
        message: wrongOnce.current ? "Ik heb het al fout gedaan, help me." : "Geef me een hint alsjeblieft.",
      });
      const reply = data?.reply || "Denk goed na, je kan het!";
      setHint(reply);
      speak(reply);
    } catch {
      const reply = "Denk goed na en kijk nog eens goed. Jij kan het!";
      setHint(reply); speak(reply);
    } finally {
      setHintLoading(false);
    }
  };

  const handle = (val) => {
    if (status === "correct") return;
    setChosen(val);
    if (isCorrectVal(val)) {
      setStatus("correct");
      sound.correct();
      speak(pick(PRAISE));
      setTimeout(() => onComplete(!wrongOnce.current), 950);
    } else {
      wrongOnce.current = true;
      setStatus("wrong");
      sound.wrong();
      speak(pick(RETRY));
      setTimeout(() => { setStatus("idle"); setChosen(null); }, 650);
    }
  };

  const chosenKey = (val) => (round.kind === "word" ? val.word : val);

  return (
    <div className="flex flex-col items-center gap-6 w-full">
      {/* Question visual */}
      <div className="flex min-h-[120px] flex-col items-center justify-center gap-3">
        {round.kind === "count" && <EmojiGroup emoji={round.emoji} n={round.count} />}
        {round.kind === "sum" && (
          round.visual ? (
            <div className="flex items-center gap-3 text-4xl sm:text-5xl font-display font-bold text-slate-700">
              <EmojiGroup emoji={round.emoji} n={round.a} />
              <span>+</span>
              <EmojiGroup emoji={round.emoji} n={round.b} />
            </div>
          ) : (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="font-display text-6xl sm:text-7xl font-bold text-slate-800 tabular-nums"
            >
              {round.a} {round.op} {round.b}
            </motion.div>
          )
        )}
        {round.kind === "pattern" && (
          <div className="flex items-center gap-2 text-5xl">
            {round.display.map((c, i) => (
              <motion.span key={i} initial={{ scale: 0 }} animate={{ scale: 1 }}
                transition={{ delay: i * 0.08 }} className="select-none">{c}</motion.span>
            ))}
          </div>
        )}
        {round.kind === "flag" && (
          <motion.span initial={{ scale: 0, rotate: -10 }} animate={{ scale: 1, rotate: 0 }}
            className="text-8xl drop-shadow select-none">{round.flag}</motion.span>
        )}
        {round.kind === "classify" && (
          <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }}
            className="text-7xl drop-shadow select-none">{round.item}</motion.span>
        )}
        {round.kind === "quiz" && (
          <motion.button
            data-testid="replay-audio-btn"
            whileTap={{ scale: 0.97 }} onClick={speakPrompt}
            className="flex items-start gap-3 rounded-3xl bg-white px-6 py-5 shadow-lg text-left tactile max-w-md"
          >
            <Volume2 className="mt-1 h-7 w-7 shrink-0 text-sky-500" />
            <span className="font-display text-lg font-bold text-slate-700">{round.question}</span>
          </motion.button>
        )}
        {round.kind === "colormix" && (
          <div className="flex items-center gap-3 text-6xl">
            <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }}>{round.a}</motion.span>
            <span className="text-4xl font-display font-bold text-slate-500">+</span>
            <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.1 }}>{round.b}</motion.span>
            <span className="text-4xl font-display font-bold text-slate-500">=</span>
            <span className="text-5xl">❓</span>
          </div>
        )}
        {(round.kind === "letter" || round.kind === "word" || round.kind === "color") && (
          <motion.button
            data-testid="replay-audio-btn"
            whileTap={{ scale: 0.9 }} whileHover={{ scale: 1.05 }}
            onClick={speakPrompt}
            className="flex flex-col items-center gap-2 rounded-3xl bg-white px-8 py-6 shadow-lg tactile"
          >
            <Volume2 className="h-12 w-12 text-sky-500" />
            <span className="font-display font-bold text-slate-500">Luister</span>
          </motion.button>
        )}
      </div>

      {/* Prompt text */}
      {round.kind !== "quiz" && (
        <p data-testid="round-prompt" className="font-display text-xl font-bold text-slate-700 text-center">
          {round.prompt}
        </p>
      )}

      {/* Options */}
      <div className={`grid w-full gap-3 ${round.kind === "word" || round.kind === "flag" || round.kind === "classify" || round.kind === "quiz" ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3"} max-w-md`}>
        {round.options.map((opt, i) => {
          const key = chosenKey(opt);
          const isChosen = chosen !== null && chosenKey(chosen) === key;
          const showWrong = isChosen && status === "wrong";
          const showRight = isChosen && status === "correct";
          const label = round.kind === "word" ? opt.emoji : opt;
          const isEmojiBig = ["word", "pattern", "color", "colormix"].includes(round.kind);
          const isText = round.kind === "flag" || round.kind === "classify" || round.kind === "quiz";
          const sizeClass = isEmojiBig ? "text-5xl py-6" : isText ? "text-lg sm:text-xl py-4" : "text-3xl";
          return (
            <motion.div key={i} animate={showWrong ? { x: [0, -8, 8, -8, 8, 0] } : {}} transition={{ duration: 0.4 }}>
              <SquishyButton
                data-testid={`option-${i}`}
                onClick={() => handle(opt)}
                color={showRight ? "#4ADE80" : showWrong ? "#F87171" : "#FFFFFF"}
                edge={showRight ? "#16A34A" : showWrong ? "#DC2626" : "#CBD5E1"}
                textClass={showRight || showWrong ? "text-white" : "text-slate-700"}
                className={`w-full ${sizeClass}`}
              >
                {label}
              </SquishyButton>
            </motion.div>
          );
        })}
      </div>

      {/* Luna hint buddy */}
      <div className="flex flex-col items-center gap-3 w-full">
        <motion.button
          data-testid="hint-btn"
          whileTap={{ scale: 0.94 }} whileHover={{ scale: 1.04 }}
          onClick={askLuna}
          disabled={hintLoading || status === "correct"}
          className="flex items-center gap-2 rounded-full bg-amber-100 px-5 py-2.5 font-display font-bold text-amber-700 shadow-sm disabled:opacity-60"
        >
          <Lightbulb className="h-5 w-5" />
          {hintLoading ? "Luna denkt na…" : "Hulp van Luna"}
        </motion.button>
        <AnimatePresence>
          {hint && (
            <motion.div
              data-testid="hint-bubble"
              initial={{ opacity: 0, y: 8, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0 }}
              className="relative max-w-md rounded-3xl bg-white px-5 py-4 text-center font-display font-semibold text-slate-700 shadow-lg"
            >
              <span className="mr-1">🦉</span> {hint}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ChoiceRound;
